import { useAuth } from '@merq/web-ui/dist/providers/userProvider';
import AccountIcon from '@mui/icons-material/AccountCircleRounded';
import {
  canView,
  inQubatorAccess,
  isWebsiteAdmin,
  useOauthPermissions,
} from 'hooks/usePermissions';
import { useTranslation } from 'react-i18next';
import useStore from 'store';

export interface RootItem {
  title: string;
  items: MenuItem[];
  icon?: React.ReactNode;
  path?: string;
  target?: string;
}

export interface MenuItem {
  title: string;
  path: string;
  target?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  id?: string;
}

const useMenu = (onLogout: () => void) => {
  const { permissions } = useOauthPermissions();
  const indexPages = useStore(state => state.indexPages.value);
  const { user } = useAuth();
  const isMerqUser = (user?.email || '').endsWith('@merqube.com');
  const { t } = useTranslation();
  const defaultMenu: RootItem[] = [
    {
      title: 'Indices',
      items: [{ title: t('navbar.indexFinder'), path: '/indices' }],
    },
    {
      title: t('navbar.about'),
      items: [
        { title: t('navbar.ourStory'), path: '/about/our-story' },
        { title: t('navbar.ourTeam'), path: '/about/our-team' },
        { title: t('navbar.careers'), path: '/about/careers' },
      ],
    },
    {
      title: t('navbar.governance'),
      items: [
        { title: t('navbar.announcements'), path: '/governance/announcements' },
        { title: t('navbar.consultations'), path: '/governance/consultations' },
        { title: t('navbar.policies'), path: '/governance/policies' },
        { title: t('navbar.supplementalData'), path: '/governance/supplemental-data' },
      ],
    },
    {
      title: 'Qubits',
      items: [
        { title: t('navbar.coverage'), path: '/news/coverage' },
        { title: t('navbar.pressReleases'), path: '/news/press' },
        { title: t('navbar.insights'), path: '/news/blog' },
        { title: t('navbar.faqs'), path: '/faqs' },
      ],
    },
  ];

  const contactItem: MenuItem = {
    title: t('navbar.contact'),
    path: '/contact',
  };

  const mainMenu = defaultMenu.map(item => {
    if (item.title === 'Indices') {
      return {
        ...item,
        items: [
          ...item.items,
          ...indexPages
            .filter(
              (p, pos, self) => p.displayInMenu && self.findIndex(i => i.slug === p.slug) === pos
            )
            .map(page => ({
              title: page.title,
              path: `/indices/partners/${page.slug}`,
            })),
        ],
      };
    }
    return item;
  });

  const garageMenu: RootItem[] = [
    {
      title: t('navbar.theGarage'),
      path: '/garage/preview',
      items: [
        canView(permissions.website.userIndicesDashboard?.actions)
          ? { title: t('navbar.indexTools'), path: '/garage/new-index', id: 'index-tools' }
          : { title: '', path: '' },
        canView(permissions.website.userIndicesDashboard?.actions)
          ? { title: t('navbar.myIndices'), path: '/garage/my-indices', id: 'my-indices' }
          : { title: '', path: '' },
        inQubatorAccess(permissions)
          ? { title: t('navbar.theInQubator'), path: '/garage/the-inqubator', id: 'the-inqubator' }
          : { title: '', path: '' },
      ].filter(item => item.title),
    },
  ];

  const adminMenu: RootItem[] = isWebsiteAdmin(permissions)
    ? [
        {
          title: 'Admin',
          items: [
            canView(permissions.website.adminNews?.actions)
              ? { title: 'News', path: '/admin/news/coverage' }
              : { title: '', path: '' },
            canView(permissions.website.adminDocuments?.actions)
              ? {
                  title: 'Consultations',
                  path: '/admin/consultations',
                }
              : { title: '', path: '' },
            canView(permissions.website.adminDocuments?.actions)
              ? {
                  title: 'Documents',
                  path: 'https://internal.api.merqube.com/admin-app/documents',
                  target: '_blank',
                }
              : { title: '', path: '' },
            canView(permissions.website.adminIndices?.actions)
              ? {
                  title: 'Indices',
                  path: 'https://internal.api.merqube.com/admin-app/index-list',
                  target: '_blank',
                }
              : { title: '', path: '' },
            canView(permissions.website.adminIndices?.actions)
              ? { title: 'Partner Indices', path: '/admin/landing-pages' }
              : { title: '', path: '' },
            canView(permissions.website.adminTeam?.actions)
              ? { title: 'Team', path: '/admin/team' }
              : { title: '', path: '' },
          ].filter(item => item.title),
        },
      ]
    : [];

  const supportMenu: RootItem[] = [
    {
      title: t('navbar.support'),
      items: [
        {
          title: t('navbar.supportCenter'),
          path: 'https://support.merqube.com/',
          target: '_blank',
        },
        {
          title: t('navbar.docsAndGuides'),
          path: '/support/documents',
        },
      ],
    },
  ];

  const userMenu: RootItem[] = [
    {
      title: t('navbar.myAccount'),
      icon: <AccountIcon sx={{ fontSize: '20px' }} />,
      items: [
        isMerqUser
          ? { title: t('navbar.myAccount'), path: '/account/profile' }
          : { title: '', path: '' },
        canView(permissions.website.apikeys?.actions)
          ? { title: t('navbar.apiKeys'), path: '/account/api-keys' }
          : { title: '', path: '' },
        { title: t('navbar.logout'), path: '', onClick: onLogout, id: 'logout' },
      ].filter(item => item.title),
    },
  ];

  return { mainMenu, adminMenu, userMenu, supportMenu, contactItem, garageMenu };
};

export default useMenu;
